import APIClient from './index';
import _ from 'lodash';
import { toApiError } from './api_error';

export const getProduct = (id, params = { fullData: 1 }, options = {}) => {
  return new Promise((resolve, reject) => {
    APIClient.get(`Products/${id}`, { params, ...options })
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

/*
  Query searches are memoized briefly so remounts and duplicate listeners replay the same
  promise instead of re-hitting Algolia; consumers must not mutate the resolved data.
  forceFresh tells the server to bypass its whitelist cache and drops the entire memo —
  a mutation stales every cached filter set, not just the one being refreshed.
*/
const productSearchCache = new Map();
const PRODUCT_SEARCH_CACHE_TTL_MS = 60 * 1000;
const PRODUCT_SEARCH_CACHE_MAX_ENTRIES = 50;

export const getProducts = (data = {}, { forceFresh = false } = {}) => {
  const performRequest = () =>
    new Promise((resolve, reject) => {
      APIClient.get(`Products`, { params: forceFresh ? { ...data, forceFresh: 1 } : data })
        .then(response => resolve(response.data))
        .catch(err => reject(_.get(err, 'data.error')));
    });

  if (forceFresh) productSearchCache.clear();
  if (!data.query) return performRequest();

  const cacheKey = JSON.stringify(_.toPairs(data).sort());
  if (!forceFresh) {
    const cached = productSearchCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.promise;
  }

  const promise = performRequest();
  promise.catch(() => {
    // Only evict our own entry — a late rejection must not delete a fresh replacement
    if (productSearchCache.get(cacheKey)?.promise === promise) productSearchCache.delete(cacheKey);
  });
  productSearchCache.set(cacheKey, { promise, expiresAt: Date.now() + PRODUCT_SEARCH_CACHE_TTL_MS });
  if (productSearchCache.size > PRODUCT_SEARCH_CACHE_MAX_ENTRIES) {
    productSearchCache.delete(productSearchCache.keys().next().value);
  }
  return promise;
};

export const getProductsForBrand = brandName => {
  return new Promise((resolve, reject) => {
    APIClient.post('Products/for_brand', { brand: brandName })
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const addProduct = data => {
  return new Promise((resolve, reject) => {
    APIClient.post(`Products`, data)
      .then(response => resolve(response.data))
      .catch(err => reject(toApiError(err)));
  });
};

export const updateProduct = (id, data) => {
  return new Promise((resolve, reject) => {
    APIClient.put(`Products/${id}`, data)
      .then(response => resolve(response.data))
      .catch(err => reject(toApiError(err)));
  });
};

export const updateProductImage = (id, data) => {
  return new Promise((resolve, reject) => {
    APIClient.put(`ProductImages/${id}`, data)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const deleteProduct = (id, params = {}, options = {}) => {
  return new Promise((resolve, reject) => {
    APIClient.delete(`Products/${id}`, { params, ...options })
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const updateVariant = (id, data = {}) => {
  return new Promise((resolve, reject) => {
    APIClient.put(`Variants/${id}`, data)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};

export const updatePin = (id, data) => {
  return new Promise((resolve, reject) => {
    APIClient.put(`Pins/${id}`, data)
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};
export const updateProductTags = (Product_id, tagIds) => {
  return new Promise((resolve, reject) => {
    APIClient.post(`ProductTags/updateTagsForProduct`, { Product_id, tagIds })
      .then(response => resolve(response.data))
      .catch(err => reject(_.get(err, 'data.error')));
  });
};
